import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { UserInputs, consultSchema, ShowAddressAndLink, consultInitialValues } from '.';
import {
    callVerifyHuman,
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    appendGoogleLinkerParam,
    getLeadsCaptureEcho,
    safeFullStory as FullStory,
} from '../../../utils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import './Consult.css';
import { useScrollToAnchor } from '../../../hooks/builder/useScrollToAnchor';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    SelfScheduleSubmitting,
    AttributionSurvey,
    LocationsDropdownWrapper,
    MarketingConsentMsg,
} from '../shared';
import { trackSubmitLeadTrackingForm } from '../../../optimizely/optimizely';
import { handleFormNavigation } from '../../../hooks/useFormPrePopulation';
import { defaultConsultConfig } from './consultFormConfigs';
import { CONSULT_FORM_ID } from '../../../utils/selfScheduleConfig';
import { useSelfScheduleTimer, SELF_SCHEDULE_EVENTS } from '../../../hooks/useSelfScheduleTimer';
import { useConsultFormConfig } from '../../../hooks/useConsultFormConfig';
import { useCloudampDataRef } from '../../../hooks/useCloudampData';
import { useFormPromoText } from '../../../hooks/useFormPromoText';
import { DEFAULT_FORM_SUBTITLE } from '../../../utils/formPromoConfig';

const CONSULT_TYPE = { q: 'Question', ss: 'Self Schedule' };

// promoPlacement opts this form into the GATSBY_FORM_PROMOS line for that
// placement (WEBDEV-4371), shown in place of DEFAULT_FORM_SUBTITLE while a campaign
// is live; see src/utils/formPromoConfig.ts.
export function ConsultationForm({ siteData, config: configProp = defaultConsultConfig, promoPlacement }) {
    const config = useConsultFormConfig(configProp);
    const promoText = useFormPromoText(promoPlacement);
    const subtitle = promoPlacement ? (promoText ?? DEFAULT_FORM_SUBTITLE) : null;
    const cloudampDataRef = useCloudampDataRef();
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;

    const isBrowser = typeof window !== 'undefined';

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    // WEBDEV-3553: Tracks when we're in the process of redirecting to the self-scheduler.
    // Formik automatically resets isSubmitting to false after the onSubmit handler resolves,
    // even though the browser hasn't navigated away yet. Without this flag, there's a brief
    // window (~100ms during the GTM event delay) where isSubmitting is false but the page is
    // still visible — causing the form fields to flash before the redirect completes.
    // Setting isRedirecting=true keeps SelfScheduleSubmitting visible until navigation occurs.
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });
    const { startTimer, pushTimingEvent, resetTimer } = useSelfScheduleTimer();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('corporateConsult', executeRecaptcha);
        return user;
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('consult-form');
        helpers.setSubmitting(true);

        // Start timer for self-schedule flow
        if (values.consultType === CONSULT_TYPE.ss) {
            startTimer({
                form_type: 'consultation',
            });
        }

        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            const city = siteData.selectedStore?.metro?.name;
            const state = siteData.selectedStore?.state?.abbreviation;
            const cloudamp__data__c =
                cloudampDataRef?.current?.value || localStorage.getItem('cloudamp__data__c') || '';
            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            } else {
                // Validate phone and email with PossibleNow
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    values.phone,
                    values.email
                );

                const formData = {
                    ...values,
                    // All four consult forms post consultType 'Self Schedule', so formSource is
                    // what lets Salesforce tell them apart.
                    formSource: 'consultation',
                    updates: true, // Always true for TCPA compliance
                    city,
                    state,
                    cloudamp__data__c,
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                    // Clicking the CTA is the opt-in (WEBDEV-4145/4146); there is no checkbox.
                    marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                };
                trackSubmitLeadTrackingForm();
                // Submit to Netlify function
                const response = await axios.post(apiEndpoint, formData);

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                    syncPardotTracking(values);
                }

                // FullStory Event Tracking
                FullStory('trackEvent', {
                    name: 'Consultation Form - Success',
                    properties: {
                        name: values.consultType,
                        user_email: values.email,
                        sms_optin: CTA_CLICK_SMS_OPT_IN,
                    },
                });

                // If backend returned a Self-Schedule URL, perform a top-level redirect
                if (isBrowser && values.consultType === CONSULT_TYPE.ss && response.data.redirectUrl) {
                    // Lock the overlay in place before the GTM await. Formik will reset
                    // isSubmitting=false when this async handler returns, so isRedirecting
                    // is what keeps SelfScheduleSubmitting visible until the browser navigates.
                    setIsRedirecting(true);
                    // Push timing event to GTM before redirect
                    // Wait for event to be sent before redirecting
                    await pushTimingEvent(SELF_SCHEDULE_EVENTS.TIMING, {
                        form_type: 'consultation',
                    });

                    helpers.resetForm();
                    let redirectUrl = response.data.redirectUrl;
                    // Append GA4 cross-domain linker parameter for tracking
                    redirectUrl = appendGoogleLinkerParam(redirectUrl);
                    window.location.href = redirectUrl;
                    return;
                } else {
                    // Store form values for use in attribution survey - only for Ask Question action
                    setFormValues({
                        formType: 'question',
                        ...formData,
                        ...getLeadsCaptureEcho(response),
                    });
                    helpers.setSubmitting(false);
                    helpers.setStatus('Success');
                    // Show attribution survey instead of immediate success
                    setShowAttributionSurvey(true);
                    helpers.resetForm();
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            // Track failure and reset timer if self-schedule submission failed
            if (values.consultType === CONSULT_TYPE.ss) {
                await pushTimingEvent(SELF_SCHEDULE_EVENTS.FAILURE, {
                    form_type: 'consultation',
                    error_type: errorMessage,
                });
                await resetTimer();
            }

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setIsRedirecting(false);
            setFormValues(null);
            setShowAttributionSurvey(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Consultation Form - Error',
                properties: {
                    name: 'Consultation',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Consultation Form - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });

            setTimeout(() => {
                helpers.resetForm();
            }, 5000);
        }
    };

    const handleAttributionComplete = useCallback(() => {
        setShowAttributionSurvey(false);

        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    }, []);

    const navigateToExternalForm = useCallback((values) => {
        handleFormNavigation({
            navigationPath: '/specials/estimate',
            formValues: values,
            eventName:
                typeof window !== 'undefined'
                    ? `${window?.location?.pathname} Form to Estimate Navigation`
                    : 'Form to Estimate Navigation',
            storageKey: 'form_data',
        });
    }, []);

    return (
        <section
            id={CONSULT_FORM_ID}
            className={`hr-divider hr-divider-consult position-relative container-fluid ${config.id}`}
        >
            {/* anchor with id="consult" should always be present */}
            <span id="consult" className="anchor-tag"></span>
            {!config.isLegacy && <span id="googlelp-quote" className="anchor-tag"></span>}
            <h2
                className={`${config.isLegacy ? 'fw-normal' : `mcl-font-greycliff ${subtitle ? 'mb-2' : 'mb-4'}`} text-center`}
            >
                {config.title}
            </h2>
            {subtitle && <p className="text-center mb-4">{subtitle}</p>}
            <Formik
                initialValues={consultInitialValues}
                validationSchema={consultSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {
                    // This way we can get access to all formik props
                    (formik) => {
                        return (
                            <Form className="fs-mask" method="POST">
                                <div className="container">
                                    <div className={config.isWide ? 'row justify-content-center' : ''}>
                                        <div className={config.isWide ? 'col-xl-10' : ''}>
                                            <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                            {(formik.isSubmitting && formik.values.consultType === CONSULT_TYPE.ss) ||
                                            isRedirecting ? (
                                                <SelfScheduleSubmitting />
                                            ) : null}
                                            {formik.isSubmitting && formik.values.consultType !== CONSULT_TYPE.ss && (
                                                <FormSubmitting />
                                            )}
                                            {!isRedirecting && formik.status === 'Failed' && <FormFailed />}
                                            {/* Only for Ask Question Action */}
                                            {formValues && !formik.isSubmitting && formik.status !== 'Failed' && (
                                                <>
                                                    {showAttributionSurvey ? (
                                                        <div className="row justify-content-center">
                                                            <div className="col-12 ">
                                                                <AttributionSurvey
                                                                    formValues={formValues}
                                                                    onComplete={handleAttributionComplete}
                                                                    isFullWidth={false}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <FormSuccess />
                                                    )}
                                                </>
                                            )}

                                            {/* Original form content - only shown when not in special states */}
                                            {!formik.isSubmitting &&
                                                !isRedirecting &&
                                                formik.status !== 'Failed' &&
                                                !formValues && (
                                                    <>
                                                        <UserInputs formik={formik} />

                                                        <div className="row locations-container">
                                                            <div className="col locations-wrapper toggle">
                                                                <div
                                                                    id="locations-dropdown"
                                                                    className="row justify-content-center my-3"
                                                                >
                                                                    <div className="col-md-6 col-lg-4">
                                                                        <h3
                                                                            className={
                                                                                config.isLegacy ? '' : 'text-center'
                                                                            }
                                                                        >
                                                                            Select a location from the list
                                                                        </h3>
                                                                        <LocationsDropdownWrapper
                                                                            {...{ siteData, formik }}
                                                                            fieldName="location"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="row justify-content-center selected-location-container mb-3">
                                                                    <div
                                                                        className={` selected-location-wrapper toggle`}
                                                                    >
                                                                        <div className="mb-0 text-center">
                                                                            {formik.values.location &&
                                                                                siteData.milanStores.length > 0 &&
                                                                                siteData.selectedStore.store && (
                                                                                    <ShowAddressAndLink
                                                                                        selectedStore={
                                                                                            siteData.selectedStore
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            {formik.values.consultType ===
                                                                                CONSULT_TYPE.ss &&
                                                                                !siteData.selectedStore?.store
                                                                                    ?.open && (
                                                                                    <div className="d-flex justify-content-center mb-3">
                                                                                        <div
                                                                                            className="alert alert-warning col text-center p-2 mb-0"
                                                                                            role="alert"
                                                                                        >
                                                                                            Selected location is not
                                                                                            open yet, but you can still
                                                                                            <span
                                                                                                onClick={() => {
                                                                                                    formik.setFieldValue(
                                                                                                        'consultType',
                                                                                                        CONSULT_TYPE.q
                                                                                                    );
                                                                                                }}
                                                                                            >
                                                                                                &nbsp;submit a question
                                                                                            </span>
                                                                                            ! Or select a different
                                                                                            location.
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row justify-content-center mb-3">
                                                            <div
                                                                className={`col-12 ${
                                                                    config.isLegacy
                                                                        ? 'lh-1 consultation-consent-msg'
                                                                        : 'mcl-font-proxima googlelp-consent-msg'
                                                                }`}
                                                            >
                                                                <MarketingConsentMsg
                                                                    cta={
                                                                        formik.values.consultType === CONSULT_TYPE.q ? (
                                                                            'Send Question'
                                                                        ) : (
                                                                            <>
                                                                                {config.buttonLabels.inquiry}
                                                                                <sup>*</sup> or{' '}
                                                                                {config.buttonLabels.schedule}
                                                                                <sup>**</sup>
                                                                            </>
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        <div
                                                            id="actions-btns"
                                                            className="row flex-sm-row-reverse gx-1 gy-2 justify-content-center"
                                                        >
                                                            <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                                {
                                                                    // Initial setup OR Self Schedule - show submit button
                                                                    (formik.values.consultType === '' ||
                                                                        formik.values.consultType ===
                                                                            CONSULT_TYPE.ss) && (
                                                                        <button
                                                                            id="corporate-schedule-btn"
                                                                            type="submit"
                                                                            className="milan-btn milan-red-btn w-100"
                                                                            onClick={() => {
                                                                                formik.setFieldValue(
                                                                                    'consultType',
                                                                                    CONSULT_TYPE.ss
                                                                                );
                                                                            }}
                                                                            disabled={
                                                                                !formik.isValid ||
                                                                                formik.status === 'BOT'
                                                                                //  || !siteData.selectedStore?.store?.open
                                                                            }
                                                                        >
                                                                            {formik.isSubmitting ? (
                                                                                'Submitting...'
                                                                            ) : (
                                                                                <>
                                                                                    {config.buttonLabels.schedule}{' '}
                                                                                    <sup>** </sup>
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    )
                                                                }
                                                                {
                                                                    // CONSULT TYPE is Question
                                                                    formik.values.consultType === CONSULT_TYPE.q && (
                                                                        <button
                                                                            type="button"
                                                                            className="milan-btn milan-fallback-btn w-100"
                                                                            onClick={() => {
                                                                                formik.setFieldValue('consultType', '');
                                                                                formik.setStatus(undefined);
                                                                            }}
                                                                        >
                                                                            Go Back
                                                                        </button>
                                                                    )
                                                                }
                                                            </div>

                                                            {config.showOrDivider && (
                                                                <div className="d-none d-sm-block col-sm-2 col-lg-1 text-center form-or-divider">
                                                                    <div className="my-3 my-md-2 fw-bold">OR</div>
                                                                </div>
                                                            )}

                                                            <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                                {
                                                                    // Show Ask A Question button when not in Question mode
                                                                    (formik.values.consultType === '' ||
                                                                        formik.values.consultType ===
                                                                            CONSULT_TYPE.ss) && (
                                                                        <button
                                                                            type="button"
                                                                            className="milan-btn milan-navy-btn w-100"
                                                                            id="ask-a-question"
                                                                            onClick={() => {
                                                                                if (config.redirectOnInquiry) {
                                                                                    navigateToExternalForm(
                                                                                        formik.values
                                                                                    );
                                                                                } else {
                                                                                    formik.setFieldValue(
                                                                                        'consultType',
                                                                                        CONSULT_TYPE.q
                                                                                    );
                                                                                }
                                                                            }}
                                                                        >
                                                                            {config.buttonLabels.inquiry} <sup>*</sup>
                                                                        </button>
                                                                    )
                                                                }
                                                                {
                                                                    // Ask A Question clicked - show submit button
                                                                    formik.values.consultType === CONSULT_TYPE.q && (
                                                                        <button
                                                                            className="milan-btn milan-navy-btn w-100 g-recaptcha"
                                                                            type="submit"
                                                                            id="send-question"
                                                                            disabled={
                                                                                !formik.isValid ||
                                                                                formik.status === 'BOT'
                                                                            }
                                                                        >
                                                                            {formik.isSubmitting
                                                                                ? 'Submitting...'
                                                                                : 'Send Question'}
                                                                        </button>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }
                }
            </Formik>
        </section>
    );
}

export default ConsultationForm;
